import { useEffect, useState } from 'react'
import { Alert, Chip, CircularProgress, Sheet, Stack, Typography } from '@mui/joy'
import { CrmFact, FACT_CATEGORY_COLORS, FACT_CATEGORY_LABELS, getCompanyFacts } from 'api/oracleApi'

interface Props {
    companyId: string
}

export function CompanyFactsList({ companyId }: Props) {
    const [facts, setFacts] = useState<CrmFact[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setLoading(true)
        setError(null)
        getCompanyFacts(companyId)
            .then(setFacts)
            .catch((err) => setError(err?.message || 'Failed to load facts'))
            .finally(() => setLoading(false))
    }, [companyId])

    if (loading)
        return (
            <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress />
            </Stack>
        )
    if (error) return <Alert color="danger">{error}</Alert>
    if (facts.length === 0)
        return (
            <Typography level="body-sm" color="neutral">
                No facts captured yet
            </Typography>
        )

    const active = facts.filter((f) => !f.invalid_at)
    const inactive = facts.filter((f) => f.invalid_at)

    return (
        <Stack gap={1.5}>
            <Typography level="title-sm">
                Facts ({active.length} active{inactive.length > 0 ? `, ${inactive.length} historical` : ''})
            </Typography>
            {active.map((fact) => (
                <FactCard key={fact.fact_id} fact={fact} />
            ))}
            {inactive.length > 0 && (
                <>
                    <Typography level="body-xs" color="neutral" sx={{ mt: 2 }}>
                        Historical (superseded)
                    </Typography>
                    {inactive.map((fact) => (
                        <FactCard key={fact.fact_id} fact={fact} dimmed />
                    ))}
                </>
            )}
        </Stack>
    )
}

function FactCard({ fact, dimmed = false }: { fact: CrmFact; dimmed?: boolean }) {
    const date = new Date(fact.valid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 1.25, opacity: dimmed ? 0.6 : 1 }}>
            <Stack direction="row" gap={1} alignItems="flex-start">
                <Chip size="sm" color={FACT_CATEGORY_COLORS[fact.category]} variant="soft" sx={{ flexShrink: 0, mt: 0.25 }}>
                    {FACT_CATEGORY_LABELS[fact.category]}
                </Chip>
                <Stack gap={0.25} sx={{ flex: 1 }}>
                    <Typography level="body-sm">{fact.content}</Typography>
                    <Typography level="body-xs" color="neutral">
                        {fact.source_type} | {date} | confidence: {fact.confidence}
                    </Typography>
                </Stack>
            </Stack>
        </Sheet>
    )
}
